import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid2, Grid4, Tag, TableChip, BtnRow, BtnPrimary,
  BtnLight, BtnSecondary, InputLabel, FormInput, FormSelect, HelpText, EmptyState, TableWrap, Th, Td,
} from "../components/ui";

const ALL_DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const PERIOD_COUNT_OPTIONS = [4,5,6,7,8,9];

function countFilled(grid) {
  let n = 0;
  Object.values(grid || {}).forEach(dayObj =>
    Object.values(dayObj || {}).forEach(v => { if (String(v || "").trim()) n++; })
  );
  return n;
}

function createEmptyGrid(days, periods) {
  const grid = {};
  days.forEach(day => {
    grid[day] = {};
    periods.forEach(p => { grid[day][p] = ""; });
  });
  return grid;
}

export default function Module11DynamicTimetable() {
  const {
    module5OwnershipMappings,
    module11State,
    setModule11State,
    getActiveAcademicYear,
    canEditModules1to5,
    module11Loading,
    module11LoadError,
    loadModule11DynamicTimetable,
    saveModule11DynamicTimetable,
    resetModule11DynamicTimetable,
  } = useACPP();

  const [periodCount, setPeriodCount] = useState(6);
  const [periodLabels, setPeriodLabels] = useState(() =>
    Array.from({ length: 6 }, (_, i) => `Period ${i + 1}`)
  );
  const [workingDays, setWorkingDays] = useState(["Monday","Tuesday","Wednesday","Thursday","Friday"]);
  const [viewMode, setViewMode] = useState("section");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [moduleStatus, setModuleStatus] = useState("Pending");
  const [savedSnapshot, setSavedSnapshot] = useState({
    sectionTimetables: {},
    teacherTimetables: {},
    periodLabels: [],
    workingDays: [],
    periodCount: 6,
  });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");

  const activeYear = getActiveAcademicYear();

  const snapshotMatchesCurrent = useMemo(() => (
    JSON.stringify(module11State || {}) === JSON.stringify(savedSnapshot || {})
  ), [module11State, savedSnapshot]);

  useEffect(() => {
    if (!module11Loading) {
      setSavedSnapshot(module11State || {
        sectionTimetables: {},
        teacherTimetables: {},
        periodLabels: [],
        workingDays: [],
        periodCount: 6,
      });
    }
  }, [module11Loading]);

  useEffect(() => {
    if (module11Loading) return;
    const nextLabels = Array.isArray(module11State.periodLabels) ? module11State.periodLabels : [];
    const nextDays = Array.isArray(module11State.workingDays) ? module11State.workingDays : [];
    if (nextLabels.length) {
      setPeriodLabels(nextLabels);
      setPeriodCount(nextLabels.length);
    }
    if (nextDays.length) {
      setWorkingDays(nextDays);
    }
  }, [module11Loading, module11State.periodLabels, module11State.workingDays]);

  useEffect(() => {
    const hasAnyGeneratedRows = Object.keys(module11State.sectionTimetables || {}).length > 0
      || Object.keys(module11State.teacherTimetables || {}).length > 0;
    const hasSavedRows = Object.keys(savedSnapshot.sectionTimetables || {}).length > 0
      || Object.keys(savedSnapshot.teacherTimetables || {}).length > 0;
    if (!hasAnyGeneratedRows && !hasSavedRows) {
      setModuleStatus("Pending");
      return;
    }
    if (snapshotMatchesCurrent) {
      setModuleStatus("Saved");
      return;
    }
    setModuleStatus("In Progress");
  }, [module11State, savedSnapshot, snapshotMatchesCurrent]);

  // rebuild period labels when period count changes
  useEffect(() => {
    setPeriodLabels(prev => {
      const next = Array.from({ length: periodCount }, (_, i) =>
        prev[i] || `Period ${i + 1}`
      );
      return next;
    });
    setSelectedEntity("");
  }, [periodCount]);

  // switch view mode → reset entity selector
  const handleViewModeChange = (e) => {
    setViewMode(e.target.value);
    setSelectedEntity("");
  };

  // ownership rows from M5
  const ownershipRows = useMemo(() =>
    module5OwnershipMappings
      .map((row) => {
        const teachers = Array.isArray(row.teachers) && row.teachers.length
          ? row.teachers
          : (String(row?.teacher_name || "").trim() ? [String(row.teacher_name).trim()] : []);
        return { ...row, teachers };
      })
      .filter((row) => row.grade && row.section && row.subject && Array.isArray(row.teachers) && row.teachers.length),
    [module5OwnershipMappings]
  );

  const ownershipStatus = ownershipRows.length
    ? `✓ ${ownershipRows.length} subject-teacher mapping(s) found in Module 5. Ready to generate.`
    : "ⓘ No ownership mappings found — complete Module 5 first, then generate.";

  // ── Metrics ─────────────────────────────────────────────────────────────
  const sectionKeys = Object.keys(module11State.sectionTimetables || {});
  const teacherKeys = Object.keys(module11State.teacherTimetables || {});
  const totalSlotsPerEntity = (module11State.workingDays?.length || 0) * (module11State.periodLabels?.length || 0);

  // ── Entity selector options ──────────────────────────────────────────────
  const entityOptions = useMemo(() => {
    if (viewMode === "section") {
      return sectionKeys.map(key => {
        const [grade, section] = key.split("__");
        return { value: key, label: `${grade} — ${section}` };
      });
    }
    return teacherKeys.map(t => ({ value: t, label: t }));
  }, [viewMode, sectionKeys, teacherKeys]);

  // ── Generate ─────────────────────────────────────────────────────────────
  const generate = useCallback(() => {
    const academicYear = activeYear;
    if (!academicYear) { alert("Please fill Academic Year in Module 1 first."); return; }
    if (!ownershipRows.length) { alert("No ownership mapping found in Module 5 yet.\n\nMake sure you have added Grade → Section → Subject → Teacher rows in Module 5 and saved them."); return; }
    if (!workingDays.length) { alert("Please select at least one working day."); return; }
    if (!periodLabels.length) { alert("Please define at least one period label."); return; }

    const newSectionTimetables = {};
    const newTeacherTimetables = {};

    // Build empty section grids
    const uniqueSectionKeys = [...new Set(ownershipRows.map(r => `${r.grade}__${r.section}`))];
    uniqueSectionKeys.forEach(key => {
      newSectionTimetables[key] = { entity: key, grid: createEmptyGrid(workingDays, periodLabels) };
    });

    // Build empty teacher grids
    const teacherSet = new Set();
    ownershipRows.forEach(r => r.teachers.forEach(t => teacherSet.add(t)));
    teacherSet.forEach(teacher => {
      newTeacherTimetables[teacher] = { entity: teacher, grid: createEmptyGrid(workingDays, periodLabels) };
    });

    // Conflict-aware fill
    const teacherBusy = {};
    teacherSet.forEach(t => {
      teacherBusy[t] = {};
      workingDays.forEach(d => { teacherBusy[t][d] = {}; });
    });

    const bySection = {};
    ownershipRows.forEach(row => {
      const key = `${row.grade}__${row.section}`;
      if (!bySection[key]) bySection[key] = [];
      bySection[key].push(row);
    });

    const allSlots = [];
    workingDays.forEach(d => periodLabels.forEach(p => allSlots.push([d, p])));
    const totalSlots = allSlots.length;

    Object.entries(bySection).forEach(([sectionKey, subjects]) => {
      const sectionBusy = {};
      workingDays.forEach(d => { sectionBusy[d] = {}; });

      subjects.forEach(row => {
        const teacher = row.teachers[0];
        let placed = 0;
        const targetSlots = Math.max(1, Math.round(totalSlots / subjects.length));

        for (let si = 0; si < totalSlots && placed < targetSlots; si++) {
          const [day, period] = allSlots[si];
          if (sectionBusy[day][period]) continue;
          if (teacher && teacherBusy[teacher]?.[day]?.[period]) continue;

          const cellText = `${row.subject}\n${teacher || ""}`.trim();
          newSectionTimetables[sectionKey].grid[day][period] = cellText;
          sectionBusy[day][period] = true;

          if (teacher && newTeacherTimetables[teacher]) {
            const [grade, section] = sectionKey.split("__");
            newTeacherTimetables[teacher].grid[day][period] = `${row.subject}\n${grade} — ${section}`;
            teacherBusy[teacher][day][period] = true;
          }
          placed++;
        }
      });
    });

    setModule11State({
      sectionTimetables: newSectionTimetables,
      teacherTimetables: newTeacherTimetables,
      workingDays,
      periodLabels,
      periodCount: periodLabels.length,
    });
    setActionError("");
    setSelectedEntity("");
  }, [activeYear, ownershipRows, workingDays, periodLabels, setModule11State]);

  // ── Reset ────────────────────────────────────────────────────────────────
  const resetModule = () => {
    setModule11State({ sectionTimetables: {}, teacherTimetables: {}, workingDays: [], periodLabels: [], periodCount: 6 });
    setActionError("");
    setSelectedEntity("");
  };

  const save = async () => {
    setSaving(true);
    setActionError("");
    try {
      const saved = await saveModule11DynamicTimetable(module11State);
      setSavedSnapshot(saved || {
        sectionTimetables: {},
        teacherTimetables: {},
        periodLabels: [],
        workingDays: [],
        periodCount: 6,
      });
      alert("Dynamic timetable saved for current ACPP year.");
    } catch (error) {
      setActionError(error?.message || "Failed to save Module 11 data.");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setSaving(true);
    setActionError("");
    try {
      const loaded = await loadModule11DynamicTimetable();
      setSavedSnapshot(loaded || {
        sectionTimetables: {},
        teacherTimetables: {},
        periodLabels: [],
        workingDays: [],
        periodCount: 6,
      });
      setSelectedEntity("");
    } catch (error) {
      setActionError(error?.message || "Failed to refresh Module 11 data.");
    } finally {
      setSaving(false);
    }
  };

  const resetYearData = async () => {
    setSaving(true);
    setActionError("");
    try {
      await resetModule11DynamicTimetable();
      setSavedSnapshot({
        sectionTimetables: {},
        teacherTimetables: {},
        periodLabels: [],
        workingDays: [],
        periodCount: 6,
      });
      setSelectedEntity("");
    } catch (error) {
      setActionError(error?.message || "Failed to reset Module 11 year data.");
    } finally {
      setSaving(false);
    }
  };

  // ── Editable cell update ─────────────────────────────────────────────────
  const handleCellChange = useCallback((day, period, value) => {
    setModule11State(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const map = viewMode === "section" ? next.sectionTimetables : next.teacherTimetables;
      if (map[selectedEntity]) map[selectedEntity].grid[day][period] = value;
      return next;
    });
  }, [viewMode, selectedEntity, setModule11State]);

  // ── Current timetable to display ────────────────────────────────────────
  const activeTimetable = useMemo(() => {
    if (!selectedEntity) return null;
    return viewMode === "section"
      ? module11State.sectionTimetables?.[selectedEntity]
      : module11State.teacherTimetables?.[selectedEntity];
  }, [viewMode, selectedEntity, module11State]);

  const displayDays = module11State.workingDays?.length ? module11State.workingDays : workingDays;
  const displayLabels = module11State.periodLabels?.length ? module11State.periodLabels : periodLabels;

  // ── Summary rows ─────────────────────────────────────────────────────────
  const summaryRows = useMemo(() => {
    const rows = [];
    const totSlots = (module11State.workingDays?.length || 0) * (module11State.periodLabels?.length || 0);
    Object.values(module11State.sectionTimetables || {}).forEach(item => {
      rows.push({
        type: "Section-wise",
        entity: item.entity.replace("__", " — "),
        workingDays: module11State.workingDays?.length || 0,
        periods: module11State.periodLabels?.length || 0,
        totalSlots: totSlots,
        filledSlots: countFilled(item.grid),
      });
    });
    Object.values(module11State.teacherTimetables || {}).forEach(item => {
      rows.push({
        type: "Teacher-wise",
        entity: item.entity,
        workingDays: module11State.workingDays?.length || 0,
        periods: module11State.periodLabels?.length || 0,
        totalSlots: totSlots,
        filledSlots: countFilled(item.grid),
      });
    });
    return rows;
  }, [module11State]);

  return (
    <ModuleCard id="module11">
      <ModuleHeader
        step="Module 11"
        title="Dynamic Timetable Builder"
        description="Build and manage section-wise and teacher-wise timetables using the existing academic ownership structure. This module remains editable and can be regenerated anytime."
        showStatus={false}
      />

      <InfoBanner>
        This timetable is built from Module 5's academic ownership layer. It uses valid grade-section-subject-teacher mappings and generates both <strong>Section Timetable</strong> and <strong>Teacher Timetable</strong> in an editable format. <strong>Note:</strong> subjects are distributed evenly across slots — edit cells directly for fine-tuning.
      </InfoBanner>
      {!!(module11LoadError || actionError) && (
        <div className="mt-3 rounded-xl border border-[#fecaca] bg-[#fff1f2] text-[#9f1239] px-3 py-2 text-sm">
          {module11LoadError || actionError}
        </div>
      )}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Academic Year" value={activeYear || "-"} meta="Active ACPP plan year" />
        <MetricCard label="Section Timetables" value={sectionKeys.length} meta="Distinct section schedules generated" />
        <MetricCard label="Teacher Timetables" value={teacherKeys.length} meta="Distinct teacher schedules generated" />
        <MetricCard label="Status" value={moduleStatus} meta={`Mapped Slots: ${sectionKeys.length * totalSlotsPerEntity}`} />
      </Grid4>

      <SectionDivider />

      <Grid2>
        {canEditModules1to5 ? (
          <SoftPanel title="Timetable Controls">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <InputLabel>Working Days</InputLabel>
              <select
                multiple
                value={workingDays}
                onChange={e => setWorkingDays(Array.from(e.target.selectedOptions).map(o => o.value))}
                className="w-full border border-[#cad7e7] rounded-xl bg-white text-[12px] p-2 min-h-[130px] outline-none focus:border-[#1565c0]"
              >
                {ALL_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <HelpText>Hold Ctrl/Cmd to select multiple days.</HelpText>
            </div>
            <div>
              <InputLabel>Periods Per Day</InputLabel>
              <FormSelect value={periodCount} onChange={e => setPeriodCount(Number(e.target.value))}>
                {PERIOD_COUNT_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </FormSelect>
              <HelpText>Controls the timetable grid size.</HelpText>
            </div>
          </div>

          <div className="mt-5">
            <p className="text-[13px] font-bold text-[#1f2937] mb-2">Period Labels</p>
            <div className="grid grid-cols-3 gap-2">
              {periodLabels.map((label, i) => (
                <div key={i}>
                  <InputLabel>Period {i + 1} Label</InputLabel>
                  <FormInput
                    value={label}
                    placeholder={`Period ${i + 1}`}
                    onChange={e => {
                      const next = [...periodLabels];
                      next[i] = e.target.value;
                      setPeriodLabels(next);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <p className={`text-[12px] mb-3 ${ownershipRows.length ? "text-[#1f8a46]" : "text-[#9ca3af] italic"}`}>{ownershipStatus}</p>
            <BtnRow>
              <BtnPrimary disabled={!canEditModules1to5 || saving} onClick={generate}>Generate Timetable</BtnPrimary>
              <BtnSecondary disabled={!canEditModules1to5 || saving} onClick={save}>Save Year Data</BtnSecondary>
              <BtnLight disabled={saving} onClick={refresh}>Refresh Year Data</BtnLight>
              <BtnLight disabled={!canEditModules1to5 || saving} onClick={resetModule}>Reset Draft</BtnLight>
            </BtnRow>
          </div>
          </SoftPanel>
        ) : null}

        <SoftPanel title="View Controls">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <InputLabel>View Mode</InputLabel>
              <FormSelect value={viewMode} onChange={handleViewModeChange}>
                <option value="section">Section-wise</option>
                <option value="teacher">Teacher-wise</option>
              </FormSelect>
            </div>
            <div>
              <InputLabel>Select Timetable</InputLabel>
              <FormSelect value={selectedEntity} onChange={e => setSelectedEntity(e.target.value)}>
                <option value="">Select timetable</option>
                {entityOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </FormSelect>
            </div>
          </div>

          <SectionDivider />
          <div className="flex flex-wrap gap-2">
            {!sectionKeys.length && !teacherKeys.length
              ? <Tag color="yellow">Generate timetable to preview section-wise and teacher-wise views</Tag>
              : <>
                  <Tag color="orange">Sections: {sectionKeys.length}</Tag>
                  <Tag color="green">Teachers: {teacherKeys.length}</Tag>
                  {module11State.workingDays?.length > 0 && (
                    <Tag color="purple">Days: {module11State.workingDays.join(", ")}</Tag>
                  )}
                  {module11State.periodLabels?.length > 0 && (
                    <Tag>{module11State.periodLabels.length} periods/day</Tag>
                  )}
                </>}
          </div>
        </SoftPanel>
      </Grid2>

      <SectionDivider />

      <SoftPanel title={canEditModules1to5 ? "Editable Timetable Grid" : "Timetable Grid"}>
        <TableWrap>
          <thead>
            <tr>
              <Th style={{ width: "80px" }}>Day</Th>
              {activeTimetable
                ? displayLabels.map(l => <Th key={l}>{l}</Th>)
                : <Th>No timetable generated yet</Th>}
            </tr>
          </thead>
          <tbody>
            {!activeTimetable ? (
              <tr><td colSpan={displayLabels.length + 1}><EmptyState>Generate the timetable first, then select a section-wise or teacher-wise view.</EmptyState></td></tr>
            ) : displayDays.map(day => (
              <tr key={day}>
                <Td><strong>{day}</strong></Td>
                {displayLabels.map(period => (
                  <td key={period} className="border border-[#e5edf8] p-1">
                    {canEditModules1to5 ? (
                      <textarea
                        value={activeTimetable.grid?.[day]?.[period] || ""}
                        disabled={!canEditModules1to5}
                        onChange={e => handleCellChange(day, period, e.target.value)}
                        className="w-full min-h-[70px] text-[12px] resize-vertical border border-[#cad7e7] rounded-lg p-1.5 outline-none focus:border-[#1565c0]"
                      />
                    ) : (
                      <div className="w-full min-h-[70px] text-[12px] rounded-lg border border-[#e5edf8] bg-[#f8fbff] p-1.5 whitespace-pre-wrap">
                        {activeTimetable.grid?.[day]?.[period] || "—"}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </TableWrap>
        {canEditModules1to5 ? (
          <p className="text-[11px] text-[#9ca3af] italic mt-2">Every cell is editable. You can update subject, teacher, or free periods directly in the timetable.</p>
        ) : null}
      </SoftPanel>

      <SectionDivider />

      {/* Leader Summary */}
      <SoftPanel title="Leader Summary">
        <TableWrap>
          <thead>
            <tr>
              <Th>Type</Th><Th>Entity</Th><Th>Working Days</Th>
              <Th>Periods / Day</Th><Th>Total Slots</Th><Th>Filled Slots</Th>
            </tr>
          </thead>
          <tbody>
            {summaryRows.length === 0 ? (
              <tr><td colSpan={6}><EmptyState>Summary will appear here after timetable generation.</EmptyState></td></tr>
            ) : summaryRows.map((r, i) => (
              <tr key={i}>
                <Td><TableChip>{r.type}</TableChip></Td>
                <Td>{r.entity}</Td>
                <Td>{r.workingDays}</Td>
                <Td>{r.periods}</Td>
                <Td>{r.totalSlots}</Td>
                <Td>{r.filledSlots}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>
    </ModuleCard>
  );
}
